import { Zap, Brain, Crown, Sparkles, Lock, Sun, Globe, Moon } from "lucide-react";
import { useT } from "@/i18n";

/**
 * 7-Vites chat speed selector — replaces the old multi-model dropdown.
 *
 *   Quick  → Gemini 3 Flash (all plans, default)
 *   Smart  → Claude Sonnet 4.6 (Creator+)
 *   Genius → GPT-5 (Pro only, counts against 400/month cap)
 *   Master → Claude Opus 4.5 (Pro only, counts against 20/month cap)
 *   Sol    → GPT 5.6 Sol   (Pro/Power — deepest reasoning of the 5.6 family)
 *   Terra  → GPT 5.6 Terra (Pro/Power — balanced power + speed)
 *   Luna   → GPT 5.6 Luna  (Pro/Power — fastest of the 5.6 family)
 *
 * Locked tiers stay tap-able so they can fire an upgrade toast/modal —
 * that's the core conversion lever (per Gemini's product idea + our extension).
 *
 * Each model is independently controlled by an admin feature flag (passed in
 * via `flagState` prop map). States: enabled / hidden / coming_soon / new.
 *   - hidden     → button not rendered at all
 *   - coming_soon → button rendered disabled with "SOON" badge
 *   - new        → button rendered with "NEW" badge
 */
const SPEED_TO_MODEL = {
  quick: "gemini-3-flash-preview",
  smart: "claude-sonnet-4-6",
  genius: "gpt-5",
  master: "claude-opus-4-5-20251101",
  sol:   "gpt-5.6-sol",
  terra: "gpt-5.6-terra",
  luna:  "gpt-5.6-luna",
};
const MODEL_TO_SPEED = Object.fromEntries(
  Object.entries(SPEED_TO_MODEL).map(([k, v]) => [v, k])
);

const TIER_RANK = { free: 0, creator: 1, pro: 2, power: 2, admin: 3 };
const meets = (planId, required) =>
  (TIER_RANK[planId] ?? 0) >= (TIER_RANK[required] ?? 0);

export default function ChatModeSelector({
  model,
  onChange,
  userPlan = "free",
  flagState = {}, // { quick: "enabled"|"hidden"|"coming_soon"|"new", smart: ..., genius: ..., master: ... }
  compact = false, // mobile chat page uses this — icon+label only, no sub, fills width
}) {
  const { t } = useT();
  const current = MODEL_TO_SPEED[model] || "quick";

  const allModes = [
    { key: "quick",  label: "Gemini 3 Flash",   short: "Quick",  sub: t("chat_mode_quick")  || "Quick · Fast",        Icon: Zap,      tier: "free" },
    { key: "smart",  label: "Claude Sonnet 4.6", short: "Smart",  sub: t("chat_mode_smart")  || "Smart · Deeper",     Icon: Brain,    tier: "creator" },
    { key: "genius", label: "GPT-5",            short: "Genius", sub: t("chat_mode_genius") || "Genius · Top reasoning", Icon: Crown, tier: "pro" },
    { key: "master", label: "Claude Opus 4.5",  short: "Master", sub: t("chat_mode_master") || "Master · Most capable",  Icon: Sparkles, tier: "pro" },
    { key: "sol",    label: "GPT 5.6 Sol",       short: "Sol",    sub: t("chat_mode_sol")    || "Sol · Deepest 5.6",       Icon: Sun,       tier: "pro" },
    { key: "terra",  label: "GPT 5.6 Terra",     short: "Terra",  sub: t("chat_mode_terra")  || "Terra · Balanced 5.6",    Icon: Globe,     tier: "pro" },
    { key: "luna",   label: "GPT 5.6 Luna",      short: "Luna",   sub: t("chat_mode_luna")   || "Luna · Fastest 5.6",      Icon: Moon,      tier: "pro" },
  ];
  // Drop hidden buttons entirely
  const modes = allModes.filter((m) => flagState[m.key] !== "hidden");

  const handleClick = (m) => {
    const state = flagState[m.key] || "enabled";
    if (state === "coming_soon") {
      // Frontend hard-block — no model switch
      return;
    }
    const locked = !meets(userPlan, m.tier);
    onChange(SPEED_TO_MODEL[m.key], { locked, mode: m });
  };

  return (
    <div
      className={
        compact
          ? "flex items-center gap-1 p-1 bg-zinc-100 rounded-lg border border-zinc-200 w-full overflow-x-auto no-scrollbar"
          : "inline-flex items-center gap-1 p-1 bg-zinc-100 rounded-lg border border-zinc-200"
      }
      data-testid="chat-mode-selector"
      role="radiogroup"
      aria-label="Chat speed"
    >
      {modes.map((m) => {
        const state = flagState[m.key] || "enabled";
        const isComingSoon = state === "coming_soon";
        const isNew = state === "new";
        const active = current === m.key && !isComingSoon;
        const locked = !meets(userPlan, m.tier);
        const isGenius = m.key === "genius";
        const isSmart = m.key === "smart";
        const isMaster = m.key === "master";
        const isSol = m.key === "sol";
        const isTerra = m.key === "terra";
        const isLuna = m.key === "luna";
        const is56 = isSol || isTerra || isLuna;

        const baseCls = compact
          ? "relative flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-bold transition-all whitespace-nowrap min-w-0"
          : "relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold transition-all";
        let stateCls;
        if (isComingSoon) {
          stateCls = "text-zinc-400 cursor-not-allowed bg-zinc-50/40";
        } else if (active && isMaster) {
          stateCls = "bg-gradient-to-r from-violet-500 via-fuchsia-500 to-purple-600 text-white shadow-md shadow-fuchsia-500/40";
        } else if (active && isSol) {
          stateCls = "bg-gradient-to-r from-orange-400 via-rose-400 to-red-500 text-white shadow-md shadow-orange-400/40";
        } else if (active && isTerra) {
          stateCls = "bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-600 text-white shadow-md shadow-teal-500/40";
        } else if (active && isLuna) {
          stateCls = "bg-gradient-to-r from-indigo-500 via-blue-500 to-sky-600 text-white shadow-md shadow-blue-500/40";
        } else if (active && isGenius) {
          stateCls = "bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 text-zinc-900 shadow-md shadow-amber-400/40";
        } else if (active && isSmart) {
          stateCls = "bg-gradient-to-r from-zinc-200 via-slate-100 to-zinc-200 text-zinc-900 border border-zinc-300 shadow-sm";
        } else if (active) {
          stateCls = "bg-white text-zinc-900 shadow-sm border border-zinc-200";
        } else if (locked) {
          stateCls = "text-zinc-500 hover:text-zinc-700 hover:bg-white/60 cursor-pointer";
        } else {
          stateCls = "text-zinc-700 hover:bg-white hover:shadow-sm";
        }

        return (
          <button
            key={m.key}
            type="button"
            role="radio"
            aria-checked={active}
            aria-disabled={isComingSoon}
            data-testid={`chat-mode-${m.key}`}
            onClick={() => handleClick(m)}
            className={`${baseCls} ${stateCls}`}
            title={`${m.label} — ${m.sub}`}
          >
            <m.Icon
              className={`w-3.5 h-3.5 ${
                isComingSoon
                  ? "text-zinc-300"
                  : active && (isMaster || is56)
                  ? "text-white"
                  : active && isGenius
                  ? "text-amber-900"
                  : active && isSmart
                  ? "text-zinc-700"
                  : isMaster
                  ? "text-fuchsia-600"
                  : isSol
                  ? "text-orange-500"
                  : isTerra
                  ? "text-teal-500"
                  : isLuna
                  ? "text-blue-500"
                  : isGenius
                  ? "text-amber-600"
                  : isSmart
                  ? "text-zinc-600"
                  : "text-emerald-600"
              }`}
              fill={isSmart && active ? "currentColor" : "none"}
            />
            <span className="flex flex-col items-start leading-tight">
              <span>{compact ? m.short : m.label}</span>
              {!compact && (
                <span className={`text-[9px] font-semibold uppercase tracking-wider ${
                  active && (isMaster || is56)
                    ? "text-white/85"
                    : active && isGenius
                    ? "text-amber-900/80"
                    : active && isSmart
                    ? "text-zinc-600"
                    : "opacity-70"
                }`}>
                  {m.sub}
                </span>
              )}
            </span>
            {!isComingSoon && locked && <Lock className="w-3 h-3 ml-0.5 opacity-70" />}
            {isComingSoon && (
              <span className="ml-1 text-[8.5px] font-black uppercase tracking-wider px-1 py-0.5 rounded bg-zinc-200 text-zinc-600">
                Soon
              </span>
            )}
            {isNew && !isComingSoon && (
              <span className="ml-1 text-[8.5px] font-black uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-500 text-white">
                New
              </span>
            )}
            {active && (isGenius || isMaster || is56) && (
              <span aria-hidden="true" className="cms-shimmer" />
            )}
          </button>
        );
      })}
      <style>{`
        .cms-shimmer {
          position: absolute;
          inset: 0;
          border-radius: 6px;
          background: linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.6) 50%, transparent 70%);
          transform: translateX(-100%);
          animation: cms-sheen 2.6s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes cms-sheen {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}

export { SPEED_TO_MODEL };
